// ─────────────────────────────────────────────────────────────────────────
// 수업 자료 — 강의자가 서고에서 담은 것. 학생은 담긴 것만 본다.
//
// 왜 클래스에 두나: 반 전체가 같은 교재를 본다. 좌석마다 두면 학생 20명에게 20번 써야 하고,
// 한 명만 실패해도 그 학생만 다른 걸 보게 된다.
//
// 어디에: classes/{classId}/lessonPlan/current (하위 컬렉션 문서)
//   클래스 문서 본체에 두지 않은 이유 — 학생(익명)이 읽어야 하는데, 클래스 본체를 열면
//   반 이름·기간뿐 아니라 adminUserIds 까지 같이 새어 나간다. 하위 문서에는 챕터 id 와
//   주차 이름만 있다.
//
// 누가 쓰나: 그 반의 **강의자 좌석 코드를 실은 요청**만(firestore.rules 의 isClassInstructor).
//   강의자는 콘솔 계정이 없는 익명 사용자다 — 자격은 좌석 코드 하나뿐이고, 그건 좌석을
//   읽을 때 이미 쓰는 신뢰 모델과 같다(코드를 아는 사람 = 그 자리의 주인).
//
// 규칙 3: 못 읽은 것과 빈 것을 구분한다. 읽기 실패는 null 로 돌려주고, 화면은 "비어 있다"가
//   아니라 "못 읽었다"로 그린다. 빈 계획(picks 0개)은 null 이 아니라 emptyPlan() 이다.
// ─────────────────────────────────────────────────────────────────────────

export const LESSON_PLAN_DOC = 'current';

// ─────────────────────────────────────────────────────────────────────────
// 담기는 두 종류를 받는다 — **서고의 책**과 **코스의 Day**.
//
// 담당자: "14일이면 1일부터 14개가 있잖아. 그걸 담는 버튼으로 담는 거지."
// 강의자에게 이 둘은 같은 일이다("오늘 쓸 것을 고른다"). 그래서 저장 구조를 나누지 않고
// **id 하나로** 담는다. picks[].chapterId 는 이제 "담긴 것의 id"다(이름은 옛 데이터와의
// 호환을 위해 그대로 둔다 — 바꾸면 저장된 계획이 사라진다).
//
//   서고 책 → 'topic.plasma'      (TOPIC_CHAPTERS 의 id)
//   코스 Day → 'day:14day:3'      (아래 dayItemId)
//
// 접두어로 갈린다. 챕터 id 는 'topic.' 으로 시작하므로 'day:' 와 절대 겹치지 않는다.
// ─────────────────────────────────────────────────────────────────────────

/** 코스 Day 를 담기 id 로. 예: dayItemId('14day', 3) → 'day:14day:3' */
export function dayItemId(courseId, day) {
  return `day:${courseId}:${day}`;
}

/** 담기 id 가 Day 인가. */
export function isDayItem(id) {
  return typeof id === 'string' && id.startsWith('day:');
}

/** Day 담기 id 를 되돌린다. Day 가 아니거나 깨졌으면 null. */
export function parseDayItemId(id) {
  if (!isDayItem(id)) return null;
  const [, courseId, dayStr] = id.split(':');
  const day = Number(dayStr);
  if (!courseId || !Number.isInteger(day) || day < 1) return null;
  return { courseId, day };
}

/** 이 코스에서 담긴 Day 번호들(오름차순). */
export function pickedDaysOf(plan, courseId) {
  return normalizePlan(plan).picks
    .map((p) => parseDayItemId(p.chapterId))
    .filter((d) => d && d.courseId === courseId)
    .map((d) => d.day)
    .sort((a, b) => a - b);
}

// 주차는 **칸**이다. 총 주차(plannedWeeks)를 정하면 1..N 칸이 처음부터 다 있고, 강의자가
// 골라서 채운다. 강의자가 "새 주차"를 만들어 가는 게 아니다 —
//   · 그래야 "16주 중 2주 채움"이 그려진다(빈 칸이 보여야 빈 줄 안다)
//   · 강의자가 순서를 건너뛰어도 된다(5주차를 먼저 준비해도 된다)
//   · 마지막 주차가 **처음부터 정의된다** — 만족도 게이트가 걸릴 자리가 확정된다
// 그래서 주차는 id 가 아니라 **번호**다.

/** 아무것도 담기지 않은 계획. "못 읽음"(null)과 다르다. */
export function emptyPlan() {
  return { picks: [], currentWeek: 1, plannedWeeks: null };
}

const asWeek = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

/**
 * 저장된 값을 안전한 모양으로. 모르는 필드는 버리고, 깨진 항목은 조용히 뺀다.
 * 옛 모양(weeks[{id,name}] + picks[].weekId)도 받아 번호로 옮긴다 —
 * 저장된 계획이 조용히 사라지면 강의자는 자기가 담은 걸 잃는다.
 */
export function normalizePlan(raw) {
  if (!raw || typeof raw !== 'object') return emptyPlan();

  // 옛 모양 → 번호. weeks 배열에서의 순서가 곧 주차 번호였다.
  const legacyOrder = new Map();
  if (Array.isArray(raw.weeks)) {
    raw.weeks.forEach((w, i) => { if (w && typeof w.id === 'string') legacyOrder.set(w.id, i + 1); });
  }

  const seen = new Set();
  const picks = (Array.isArray(raw.picks) ? raw.picks : [])
    .map((p) => {
      if (!p || typeof p.chapterId !== 'string' || !p.chapterId) return null;
      const week = asWeek(p.week) ?? legacyOrder.get(p.weekId) ?? null;
      return week ? { chapterId: p.chapterId, week } : null;
    })
    .filter(Boolean)
    // 같은 챕터가 두 칸에 담기면 처음 담은 것만 남긴다(학생 화면에 두 번 뜨지 않게).
    .filter((p) => (seen.has(p.chapterId) ? false : (seen.add(p.chapterId), true)));

  const currentWeek = asWeek(raw.currentWeek)
    ?? (raw.currentWeekId ? legacyOrder.get(raw.currentWeekId) : null)
    ?? 1;

  const plannedWeeks = asWeek(raw.plannedWeeks);
  return { picks, currentWeek, plannedWeeks };
}

/**
 * 이 반의 총 주차 — **계획에 적힌 값이 우선**이고, 없으면 좌석에서 온 값(만들 때 정한 것).
 *
 * 왜 계획에 두나: 총 주차는 좌석에도 박혀 있는데(쿠키로 흘러야 하니까), 그것만 고치면
 * **이미 들어와 있는 학생에게는 다음 입장 때까지 반영되지 않는다.** 계획은 실시간으로
 * 구독하고 있으므로, 강의자가 5주로 바꾸면 학생 화면이 그 자리에서 따라온다.
 * 좌석의 값은 첫 값(씨앗)으로 남는다 — 계획을 못 읽었을 때의 대비이기도 하다.
 */
export function effectiveWeeks(plan, seatValue = null) {
  const fromPlan = normalizePlan(plan).plannedWeeks;
  if (fromPlan) return fromPlan;
  return seatValue > 0 ? Math.floor(seatValue) : null;
}

/**
 * 담은 것 중 가장 뒤 주차 — 총 주차를 이보다 적게 줄이면 그 자료가 사라진다.
 * 줄이기를 막는 근거이자, 화면이 "N주차까지 비워야 줄일 수 있어요"라고 말할 근거.
 */
export function lastFilledWeek(plan) {
  return normalizePlan(plan).picks.reduce((m, x) => Math.max(m, x.week), 0);
}

/**
 * 총 주차를 바꾼다. 담은 것보다 적게는 못 줄인다 — 조용히 자료를 버리지 않는다.
 * @returns {{ plan: object, error: string|null }}
 */
export function setPlannedWeeks(plan, weeks) {
  const p = normalizePlan(plan);
  const n = asWeek(weeks);
  if (!n) return { plan: p, error: '주차는 1 이상의 숫자여야 합니다.' };
  const last = lastFilledWeek(p);
  if (n < last) {
    return { plan: p, error: `${last}주차에 담은 자료가 있어요. ${last}주차를 비운 뒤에 줄일 수 있습니다.` };
  }
  const currentWeek = Math.min(p.currentWeek, n);
  return { plan: { ...p, plannedWeeks: n, currentWeek }, error: null };
}

/** 담긴 챕터 id 집합. */
export function openChapterIds(plan) {
  return new Set(normalizePlan(plan).picks.map((p) => p.chapterId));
}

/** 이 챕터가 학생에게 열려 있나. */
export function isChapterOpen(plan, chapterId) {
  return openChapterIds(plan).has(chapterId);
}

/** 담긴 게 하나도 없나. */
export function planIsEmpty(plan) {
  return normalizePlan(plan).picks.length === 0;
}

/** 강의자가 지금 채울 칸을 고른다. 총 주차를 알면 그 범위를 벗어나지 못하게 한다. */
export function setCurrentWeek(plan, week, plannedWeeks = null) {
  const p = normalizePlan(plan);
  const n = asWeek(week);
  if (!n) return p;
  if (plannedWeeks > 0 && n > plannedWeeks) return p;
  return { ...p, currentWeek: n };
}

/** 챕터를 지금 고른 칸에 담는다. */
export function addPick(plan, chapterId) {
  const p = normalizePlan(plan);
  if (!chapterId) return p;
  if (p.picks.some((x) => x.chapterId === chapterId)) return p; // 이미 담김
  return { ...p, picks: [...p.picks, { chapterId, week: p.currentWeek }] };
}

/** 담은 것을 뺀다. */
export function removePick(plan, chapterId) {
  const p = normalizePlan(plan);
  return { ...p, picks: p.picks.filter((x) => x.chapterId !== chapterId) };
}

/**
 * 강의자 화면용 — **칸 전부**를 1..plannedWeeks 로 준다(빈 칸 포함, 오름차순).
 * 빈 칸이 보여야 "나머지는 비었구나"를 안다. 총 주차를 모르면 채워진 칸만 준다.
 */
export function weekSlots(plan, plannedWeeks, chapterById) {
  const p = normalizePlan(plan);
  const get = (id) => (chapterById instanceof Map ? chapterById.get(id) : (chapterById || {})[id]);
  const maxFilled = p.picks.reduce((m, x) => Math.max(m, x.week), 0);
  const total = plannedWeeks > 0 ? plannedWeeks : maxFilled;
  return Array.from({ length: total }, (_, i) => i + 1).map((week) => ({
    week,
    chapters: p.picks.filter((x) => x.week === week).map((x) => get(x.chapterId)).filter(Boolean),
  }));
}

/**
 * 학생 화면용 — **채워진 칸만**, 최근 주차가 위. 빈 칸을 학생이 볼 이유는 없다.
 * 지난 주차는 닫지 않는다(복습). 담긴 순서 = 강의자가 가르치는 순서라 칸 안에서는 그대로.
 */
export function weeksWithChapters(plan, chapterById) {
  return weekSlots(plan, null, chapterById)
    .filter((w) => w.chapters.length)
    .reverse();
}

export const GATE = Object.freeze({
  open: 'open',             // 게이트 없음 — 서고 전체가 열린다(강의자 계정을 안 쓰는 반·개인 사용자)
  instructor: 'instructor', // 강의자 — 서고 전체 + 담기
  gated: 'gated',           // 학생 — 담긴 것만
});

/**
 * 이 사람이 서고를 어떻게 보는가. 라이선스(쿠키)에서 나온 값으로만 정한다.
 * 값이 없으면 open — 무회귀(기존 반·개인 구매자는 지금까지처럼 전부 열린다).
 */
export function gateFor(license) {
  if (!license) return GATE.open;
  if (license.role === 'instructor') return GATE.instructor;
  return license.lessonGated === true ? GATE.gated : GATE.open;
}

// ─────────────────────────────────────────────────────────────────────────
// 마지막 주차 게이트 — 만족도를 내야 마지막 주차 자료가 열린다.
//
// 담당자 결정: 만족도가 없으면 운영사 보고가 비고, 반이 끝나면 학생이 흩어져 영영 못 받는다.
// 그래서 조르는 대신 **구조로** 건다(규칙 5 — 재촉하지 않는다).
//
// 마지막 주차는 **마지막 칸(plannedWeeks)** 이다. 강의자가 몇 칸을 채웠든 상관없다 —
// 칸이 처음부터 다 있으므로 마지막이 어디인지 처음부터 정해져 있다.
//
// ⚠️ 지난 주차는 **절대 막지 않는다.** 복습이 이 화면의 존재 이유고, 2차에서 "지난 주차는
//    닫히지 않는다"를 규칙으로 박았다. 게이트는 마지막 칸 **하나만** 건다.
//
// ⚠️ 총 주차를 모르면 게이트도 없다. "마지막"이 정의되지 않는데 잠그면
//    아무도 이유를 모르는 잠금이 된다.
// ─────────────────────────────────────────────────────────────────────────

/** 만족도 게이트가 걸린 주차 번호. 총 주차를 모르면 null. */
export function surveyGatedWeek(plannedWeeks) {
  return plannedWeeks > 0 ? Math.floor(plannedWeeks) : null;
}

export const LOCK = Object.freeze({
  notPicked: 'notPicked',   // 강의자가 아직 안 담았다
  needSurvey: 'needSurvey', // 마지막 주차 — 만족도를 내야 열린다
});

/**
 * 이 챕터가 왜 안 열리나. 열려 있으면 null.
 * 이유가 둘이므로 화면이 팝업을 갈라 띄울 수 있게 **이유를 돌려준다** —
 * 하나로 뭉치면 학생이 마지막 주차가 안 열리는 걸 강의자에게 물어보게 된다.
 */
export function chapterLockReason(plan, chapterId, { plannedWeeks = null, surveyDone = false } = {}) {
  const p = normalizePlan(plan);
  const pick = p.picks.find((x) => x.chapterId === chapterId);
  if (!pick) return LOCK.notPicked;
  const gated = surveyGatedWeek(plannedWeeks);
  if (gated && pick.week === gated && !surveyDone) return LOCK.needSurvey;
  return null;
}

/** 만족도를 내야 열리는 자료가 실제로 있나 — 학생에게 조사를 권할지 판단용. */
export function hasSurveyGatedChapters(plan, plannedWeeks) {
  const gated = surveyGatedWeek(plannedWeeks);
  if (!gated) return false;
  return normalizePlan(plan).picks.some((x) => x.week === gated);
}


/**
 * Day 마지막 스텝을 "이번 수업의 끝"으로 바꾼다 — 다음 Day 가 안 담겼을 때만.
 *
 * 왜 눌렀을 때 막지 않고 여기서 바꾸나: 이 스텝의 대사는 **음성으로 나간다.**
 * "Day 2 를 바로 시작하시겠어요?" 라고 권해 놓고 누르면 막히면, 그건 막힌 게 아니라
 * 고장으로 읽힌다. 애초에 권하지 않는다.
 *
 * 셋을 함께 바꾼다 — 하나라도 빠지면 어긋난다.
 *   · 선택지: "Day N 시작하기"를 빼고 마무리만 남긴다
 *   · 자막:   다음 Day 를 권하는 문구를 마무리 문구로 덮는다
 *   · 음성:   `_lessonEnd` 표시를 보고 화면이 플레이어를 끈다
 *
 * ⚠️ mp3 는 손대지 않는다. 1,600여 개가 모든 반에 공용이라 반마다 다르게 구울 수 없다.
 *    **안 트는 것으로 충분하다.**
 *
 * @param {object|null} step        대본의 현재 스텝
 * @param {number[]|null} allowedDays  열린 Day 목록. null 이면 제한 없음 → 그대로 둔다.
 * @param {number} currentDay
 * @returns 원본 스텝 또는 마무리로 바뀐 스텝
 */
export const LESSON_END_TEXT = '오늘 수업은 여기까지예요. 다음 자료는 강의자님이 열어주시면 이어서 할 수 있어요.';

export function closingStepFor(step, { allowedDays = null, currentDay = 0 } = {}) {
  if (!step || !Array.isArray(step.choices)) return step;
  if (!Array.isArray(allowedDays)) return step;          // 제한 없음
  if (allowedDays.includes(currentDay + 1)) return step;  // 다음 Day 도 담겼다
  const trimmed = step.choices.filter((c) => c.nextAction !== 'go_next_day');
  if (trimmed.length === step.choices.length) return step; // 다음 Day 권유가 없는 스텝
  return {
    ...step,
    _lessonEnd: true,
    text: LESSON_END_TEXT,
    choices: trimmed.length ? trimmed : [{ id: 'stay', label: '오늘은 여기까지', nextAction: 'complete' }],
  };
}
